import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Archive, Eraser, Search, ShieldCheck } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

/**
 * Super Admin — Demo Data Cleanup Tool.
 *
 * Three modes:
 *   • Dry run   → shows exactly what WOULD be affected, no writes
 *   • Unpublish → archives target doctors, drops slug, keeps records
 *   • Delete    → hard-deletes users + doctors + all related records
 *
 * Always protected: super_admin/admin accounts + demo-anaya-mehta slug + any
 * emails/slugs the user pins here.
 */
export default function SuperAdminMaintenance() {
    const [config, setConfig] = useState({
        email_suffixes: "@platform.test,@qa.local,@clinic.test",
        include_null_slug_published: true,
        protected_slugs: "demo-anaya-mehta",
        protected_emails: "",
    });
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [confirmText, setConfirmText] = useState("");

    function buildPayload(mode) {
        const toArr = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
        return {
            mode,
            email_suffixes: toArr(config.email_suffixes),
            include_null_slug_published: !!config.include_null_slug_published,
            protected_slugs: toArr(config.protected_slugs),
            protected_emails: toArr(config.protected_emails),
        };
    }

    async function run(mode) {
        if (mode === "delete" && confirmText !== "DELETE") {
            toast.error("Type DELETE to confirm hard-deletion.");
            return;
        }
        setBusy(true);
        setResult(null);
        try {
            const { data } = await api.post(
                "/super-admin/maintenance/cleanup-demo-data",
                buildPayload(mode),
            );
            setResult(data);
            const c = data.counts || {};
            if (mode === "dry_run") {
                toast.success(
                    `Preview: ${c.users_targeted || 0} users · ${c.doctors_targeted || 0} doctors would be affected`,
                );
            } else if (mode === "unpublish") {
                const n = data.actions_taken?.doctors_unpublished || 0;
                toast.success(`Archived ${n} doctor${n === 1 ? "" : "s"}`);
            } else {
                const u = data.actions_taken?.users_deleted || 0;
                const d = data.actions_taken?.doctors_deleted || 0;
                toast.success(`Deleted ${u} user${u === 1 ? "" : "s"} and ${d} doctor${d === 1 ? "" : "s"}`);
                setConfirmDelete(false);
                setConfirmText("");
            }
        } catch (err) {
            toast.error(errMsg(err, "Cleanup failed"));
        } finally {
            setBusy(false);
        }
    }

    return (
        <AdminShell>
            <div className="mb-6" data-testid="maintenance-header">
                <div className="text-xs uppercase tracking-widest text-[#123B5D]/60">Super admin</div>
                <h1 className="text-2xl font-bold text-[#123B5D]">Demo data cleanup</h1>
                <p className="text-[#17212B]/60 mt-1 text-sm">
                    Purge test / QA / demo accounts before production launch. Always run a dry
                    run first. Super admin, admin, and pinned demo slugs are never touched.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="border-[#123B5D]/10 lg:col-span-2" data-testid="maintenance-config-card">
                    <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                            <ShieldCheck size={16} className="text-[#0F766E]" />
                            Cleanup configuration
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label htmlFor="suffixes">Demo email suffixes (comma-separated)</Label>
                            <Input
                                id="suffixes"
                                value={config.email_suffixes}
                                onChange={(e) => setConfig({ ...config, email_suffixes: e.target.value })}
                                placeholder="@platform.test,@qa.local,@clinic.test"
                                data-testid="maintenance-suffixes"
                            />
                            <p className="text-xs text-[#17212B]/60 mt-1">
                                Users whose email ends in any of these will be candidates for cleanup.
                            </p>
                        </div>
                        <div>
                            <Label htmlFor="protected-slugs">Protected slugs (comma-separated)</Label>
                            <Input
                                id="protected-slugs"
                                value={config.protected_slugs}
                                onChange={(e) => setConfig({ ...config, protected_slugs: e.target.value })}
                                placeholder="demo-anaya-mehta"
                                data-testid="maintenance-protected-slugs"
                            />
                            <p className="text-xs text-[#17212B]/60 mt-1">
                                Doctors with these slugs will never be archived or deleted.
                            </p>
                        </div>
                        <div>
                            <Label htmlFor="protected-emails">Extra protected emails (comma-separated)</Label>
                            <Input
                                id="protected-emails"
                                value={config.protected_emails}
                                onChange={(e) => setConfig({ ...config, protected_emails: e.target.value })}
                                placeholder="doctor-to-keep@example.com"
                                data-testid="maintenance-protected-emails"
                            />
                            <p className="text-xs text-[#17212B]/60 mt-1">
                                Always protected regardless: super_admin/admin accounts, cashfree-test@platform.test.
                            </p>
                        </div>
                        <label className="flex items-center gap-2 text-sm text-[#17212B]/80">
                            <Checkbox
                                checked={config.include_null_slug_published}
                                onCheckedChange={(v) =>
                                    setConfig({ ...config, include_null_slug_published: !!v })
                                }
                                data-testid="maintenance-null-slug"
                            />
                            <span>
                                Also target published doctors with no slug (junk data).
                            </span>
                        </label>
                    </CardContent>
                </Card>

                <Card className="border-[#123B5D]/10" data-testid="maintenance-actions-card">
                    <CardHeader>
                        <CardTitle className="text-base">Actions</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <Button
                            className="w-full justify-start gap-2 bg-[#123B5D] hover:bg-[#0F2E48]"
                            onClick={() => run("dry_run")}
                            disabled={busy}
                            data-testid="maintenance-dry-run"
                        >
                            <Search size={16} />
                            {busy ? "Running…" : "Dry run — preview only"}
                        </Button>

                        <Button
                            variant="outline"
                            className="w-full justify-start gap-2 border-[#D97706] text-[#D97706] hover:bg-[#D97706]/5"
                            onClick={() => run("unpublish")}
                            disabled={busy}
                            data-testid="maintenance-unpublish"
                        >
                            <Archive size={16} />
                            Unpublish (archive, keep records)
                        </Button>

                        <div className="pt-3 border-t border-[#123B5D]/10 space-y-2">
                            <div className="text-xs text-[#DC2626] font-semibold flex items-center gap-1.5">
                                <AlertTriangle size={12} /> Destructive — no undo
                            </div>
                            {!confirmDelete ? (
                                <Button
                                    variant="outline"
                                    className="w-full justify-start gap-2 border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626]/5"
                                    onClick={() => setConfirmDelete(true)}
                                    disabled={busy}
                                    data-testid="maintenance-delete-arm"
                                >
                                    <Eraser size={16} />
                                    Hard delete users &amp; records…
                                </Button>
                            ) : (
                                <div className="space-y-2">
                                    <Label htmlFor="confirm" className="text-xs">
                                        Type <span className="font-mono font-bold">DELETE</span> to confirm
                                    </Label>
                                    <Input
                                        id="confirm"
                                        value={confirmText}
                                        onChange={(e) => setConfirmText(e.target.value)}
                                        placeholder="DELETE"
                                        data-testid="maintenance-delete-confirm-input"
                                    />
                                    <div className="flex gap-2">
                                        <Button
                                            className="flex-1 bg-[#DC2626] hover:bg-[#B91C1C]"
                                            onClick={() => run("delete")}
                                            disabled={busy || confirmText !== "DELETE"}
                                            data-testid="maintenance-delete-confirm"
                                        >
                                            {busy ? "Deleting…" : "Confirm delete"}
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            onClick={() => {
                                                setConfirmDelete(false);
                                                setConfirmText("");
                                            }}
                                            data-testid="maintenance-delete-cancel"
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {result && (
                <div className="mt-6 space-y-4" data-testid="maintenance-result">
                    <Card className="border-[#123B5D]/10">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm text-[#123B5D]">
                                {result.dry_run ? "Dry-run preview" : `Completed · mode: ${result.mode}`}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                                <StatCell label="Users targeted" value={result.counts?.users_targeted} testId="cnt-users" />
                                <StatCell label="Doctors targeted" value={result.counts?.doctors_targeted} testId="cnt-doctors" />
                                <StatCell label="Users spared" value={result.counts?.users_spared} testId="cnt-users-spared" />
                                <StatCell label="Doctors spared" value={result.counts?.doctors_spared} testId="cnt-doctors-spared" />
                                <StatCell label="Null-slug extras" value={result.counts?.null_slug_published_extra} testId="cnt-null-slug" />
                            </div>
                        </CardContent>
                    </Card>

                    {!result.dry_run && (
                        <Card className="border-[#123B5D]/10">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm">Actions performed</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                    {Object.entries(result.actions_taken || {}).map(([k, v]) => (
                                        <div
                                            key={k}
                                            className="flex justify-between border-b border-[#123B5D]/5 py-1"
                                            data-testid={`action-${k}`}
                                        >
                                            <span className="font-mono text-[#17212B]/70">{k}</span>
                                            <span className="font-bold text-[#123B5D]">{v}</span>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {(result.targeted_users_sample?.length > 0 || result.targeted_doctors_sample?.length > 0) && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <Card className="border-[#123B5D]/10">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm">Sample users ({result.targeted_users_sample?.length || 0} shown)</CardTitle>
                                </CardHeader>
                                <CardContent className="p-0">
                                    <table className="w-full text-xs">
                                        <thead className="bg-[#F5F8FA] border-b border-[#123B5D]/10">
                                            <tr>
                                                <th className="text-left p-2">Email</th>
                                                <th className="text-left p-2">Role</th>
                                                <th className="text-left p-2">Created</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(result.targeted_users_sample || []).map((u, i) => (
                                                <tr key={i} className="border-b border-[#123B5D]/5" data-testid={`sample-user-${i}`}>
                                                    <td className="p-2 font-mono">{u.email}</td>
                                                    <td className="p-2">{u.role}</td>
                                                    <td className="p-2 text-[#17212B]/60">{u.created_at?.slice(0, 10)}</td>
                                                </tr>
                                            ))}
                                            {(result.targeted_users_sample || []).length === 0 && (
                                                <tr><td colSpan={3} className="p-3 text-center text-[#17212B]/60">No users targeted.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </CardContent>
                            </Card>

                            <Card className="border-[#123B5D]/10">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm">Sample doctors ({result.targeted_doctors_sample?.length || 0} shown)</CardTitle>
                                </CardHeader>
                                <CardContent className="p-0">
                                    <table className="w-full text-xs">
                                        <thead className="bg-[#F5F8FA] border-b border-[#123B5D]/10">
                                            <tr>
                                                <th className="text-left p-2">Name</th>
                                                <th className="text-left p-2">Slug</th>
                                                <th className="text-left p-2">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(result.targeted_doctors_sample || []).map((d, i) => (
                                                <tr key={i} className="border-b border-[#123B5D]/5" data-testid={`sample-doctor-${i}`}>
                                                    <td className="p-2">{d.full_name}</td>
                                                    <td className="p-2 font-mono text-[#17212B]/70">{d.slug || "—"}</td>
                                                    <td className="p-2">{d.profile_status}</td>
                                                </tr>
                                            ))}
                                            {(result.targeted_doctors_sample || []).length === 0 && (
                                                <tr><td colSpan={3} className="p-3 text-center text-[#17212B]/60">No doctors targeted.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    {result.spared_slugs_sample?.length > 0 && (
                        <Card className="border-[#0F766E]/30 bg-[#0F766E]/5">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm text-[#0F766E]">Protected slugs (spared)</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex flex-wrap gap-2">
                                    {result.spared_slugs_sample.map((s, i) => (
                                        <span key={i} className="text-xs bg-white border border-[#0F766E]/20 rounded-full px-2 py-1 font-mono text-[#0F766E]">
                                            {s.slug || s.full_name}
                                        </span>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}
        </AdminShell>
    );
}

function StatCell({ label, value, testId }) {
    return (
        <div className="flex flex-col border border-[#123B5D]/10 rounded-md p-3 bg-white" data-testid={testId}>
            <span className="text-xs uppercase tracking-wide text-[#17212B]/60">{label}</span>
            <span className="text-2xl font-bold text-[#123B5D] mt-1">{value ?? 0}</span>
        </div>
    );
}
